import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useViewportWidth } from '../../hooks/ui/useViewportWidth';
import { DRIVING_DISABLED_WARNING_REASON, type DrivingDisabledWarningReason } from '../../robot/driving-disabled-warning';
import { closeDrivingDisabledWarning, useDrivingDisabledWarningState } from './drivingDisabledWarningNotification';
import { useBottomSnackbarStackHeight, useBottomSnackbarStackMaxWidth, useMeasuredBottomSnackbarStackIndex } from './SnackbarStackContext';
import {
  BOTTOM_SNACKBAR_MAX_CONTENT_WIDTH_PX,
  bottomCenterSnackbarStackSx,
  fixedWidthBottomSnackbarRootSx,
  fullWidthConstrainedBottomSnackbarContentSx,
  getBottomSnackbarPlacement,
  mergeSx,
} from './snackbarStyles';

const AUTO_HIDE_DURATION_MS = 5000;
const STACK_SLOTS = 2;

const DrivingDisabledWarningSnackbar = () => {
  const { t } = useTranslation();
  const { nonce, open, reason } = useDrivingDisabledWarningState();
  const centerStackHeight = useBottomSnackbarStackHeight('center');
  const leftStackHeight = useBottomSnackbarStackHeight('left');
  const leftStackWidth = useBottomSnackbarStackMaxWidth('left');
  const theme = useTheme();
  const viewportWidth = useViewportWidth();
  const placement = getBottomSnackbarPlacement({
    desktopLayoutMinWidth: theme.breakpoints.values.sm,
    leftStackWidth,
    snackbarWidth: BOTTOM_SNACKBAR_MAX_CONTENT_WIDTH_PX,
    viewportWidth,
  });
  const stacksAboveLeftSnackbars = placement === 'stacked';
  const { ref: snackbarRef, stackIndex } = useMeasuredBottomSnackbarStackIndex<HTMLDivElement>({
    horizontal: placement === 'right' ? 'right' : 'center',
    minimumStackIndex: placement === 'right' ? centerStackHeight : stacksAboveLeftSnackbars ? leftStackHeight : 0,
    open,
    slots: STACK_SLOTS,
  });

  const messages: Record<DrivingDisabledWarningReason, string> = {
    [DRIVING_DISABLED_WARNING_REASON.WIRED_CONTROLLER]: t(
      'program.drivingDisabled.warning.wiredController',
      'Driving is disabled due to a wired controller connection with your drive base. Connect wirelessly if driving is needed for this program.'
    ),
    [DRIVING_DISABLED_WARNING_REASON.WIRED_DRIVE_BASE]: t(
      'program.drivingDisabled.warning.wiredDriveBase',
      'Driving is disabled due to a wired drive base connection. Connect your drive base wirelessly if driving is needed for this program.'
    ),
    [DRIVING_DISABLED_WARNING_REASON.CHARGING]: t(
      'program.drivingDisabled.warning.charging',
      'Driving is disabled while charging. Stop charging and connect your drive base wirelessly if driving is needed for this program.'
    ),
  };

  return (
    <Snackbar
      anchorOrigin={{ vertical: 'bottom', horizontal: placement === 'right' ? 'right' : 'center' }}
      autoHideDuration={AUTO_HIDE_DURATION_MS}
      key={nonce}
      onClose={(_event, closeReason) => {
        if (closeReason !== 'clickaway') {
          closeDrivingDisabledWarning();
        }
      }}
      open={open}
      ref={snackbarRef}
      sx={mergeSx(bottomCenterSnackbarStackSx(stackIndex), fixedWidthBottomSnackbarRootSx)}
    >
      <Alert severity="warning" sx={fullWidthConstrainedBottomSnackbarContentSx} variant="filled">
        {reason ? messages[reason] : null}
      </Alert>
    </Snackbar>
  );
};

export default DrivingDisabledWarningSnackbar;
